import usePrevious from 'hooks/usePrevious';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_DELETE_REQUEST, REMOVE_DELETE_REQUEST, UPDATE_APPLICATION_DETAIL_POST_DATA } from 'store/key';
import { generateRandomId, produce } from 'utility/general';
import { isEqual } from 'lodash';

const useStudySiteOutOfWithHAData = ({ formId = -1, siteInfoId = -1 }) => {
    const [openPopup, setOpenPopup] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const serverTableKey = 'studySiteOutOfHA';
    const remoteData = useSelector((state) => state.applicationDetail?.[formId]?.[serverTableKey]);
    const updatePostData = useSelector((state) => state.applicationDetailPostData.postData[serverTableKey]);
    const dispatch = useDispatch();
    const [rows, setRows] = useState(remoteData ?? []);
    const previousData = usePrevious(remoteData);
    const [defaultFormList] = useState({
        country: {
            value: '',
            label: 'Country / City',
            name: 'country',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
        },
        institution: {
            value: '',
            label: 'Hospital / Institution',
            name: 'institution',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
        },
        department: {
            value: '',
            label: 'Department',
            name: 'department',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
        },
        others: {
            value: '',
            label: 'If others, please specify',
            name: 'others',
            type: 'input',
            maxLength: 255,
        },
    });

    // update row when remote data update
    useEffect(() => {
        if (!isEqual(previousData, remoteData)) {
            setRows(remoteData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteData]);

    const removeAllTempData = () => {
        setRows(
            rows.filter((item) => {
                return !item.id.toString().startsWith('temp_');
            }),
        );
    };

    const getLabelName = (key) => {
        return defaultFormList[key]?.label ?? 'undefined';
    };

    const [columns] = useState([
        { field: 'country', headerName: getLabelName('country'), width: 120 },
        { field: 'institution', headerName: getLabelName('institution'), width: 150 },
        { field: 'department', headerName: getLabelName('department'), width: 150 },
        { field: 'others', headerName: getLabelName('others'), width: 300 },
    ]);

    const handleEditClick = (row) => {
        setSelectedRow(row);
        setOpenPopup(true);
    };

    const handleRowCreate = async (id) => {
        setSelectedRow(null);
        setOpenPopup(true);
    };

    const onEditSubmit = (row) => {
        let postData = updatePostData ?? [];

        const index = postData.findIndex((item) => {
            return item.id === row.id;
        });

        if (index === -1) {
            postData.push(row);
        } else {
            postData[index] = row;
        }

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: postData,
                applicationFormId: formId,
            },
        });

        //update local data
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                draft[index] = row;
            }),
        );
    };

    const onRecordUpdate = async (data) => {
        data.id = `temp_${generateRandomId()}`;
        const newRows = produce(rows, (draft) => {
            draft.push(data);
        });
        setRows(newRows);
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...(updatePostData ?? []), data],
                applicationFormId: formId,
            },
        });
    };

    const handleDelete = async (row) => {
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }),
        );

        // skip if id starts with temp_, e.g. not exist in server
        if (row.id.toString().startsWith('temp_')) {
            let postData = updatePostData ?? [];

            const index = postData.findIndex((item) => {
                return item.id === row.id;
            });

            if (index !== -1) {
                postData.splice(index, 1);
            }

            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: serverTableKey,
                    data: postData,
                    applicationFormId: formId,
                },
            });

            return;
        }

        dispatch({
            type: ADD_DELETE_REQUEST,
            payload: {
                type: serverTableKey,
                id: row.id,
                applicationFormId: formId,
            },
        });
    };

    const recoverDataCreation = async (item) => {
        // handle edited data recovery
        const updatedPostData = [];

        rows.forEach((item) => {
            const remoteDataObject = remoteData.find((remoteItem) => {
                return remoteItem.id === item.id;
            });

            if (remoteDataObject) {
                const isSame = Object.entries(item).every(([key, value]) => {
                    // remove exception
                    if (key === 'siteInfo' || key === 'applicationForm') {
                        return true;
                    }

                    return remoteDataObject[key] === value;
                });

                if (!isSame) {
                    updatedPostData.push(item);
                }
            }
        });

        const postData = rows.filter((row) => {
            const isDataCreation = row.id.toString().startsWith('temp_');

            if (!isDataCreation) {
                dispatch({
                    type: REMOVE_DELETE_REQUEST,
                    payload: {
                        type: serverTableKey,
                        id: row.id,
                        applicationFormId: formId,
                    },
                });
            }

            return isDataCreation;
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...postData, ...updatedPostData],
                applicationFormId: formId,
            },
        });
    };

    const onDataEmpty = async (item) => {
        remoteData?.forEach((row) => {
            dispatch({
                type: ADD_DELETE_REQUEST,
                payload: {
                    type: serverTableKey,
                    id: row.id,
                    siteInfoId,
                    applicationFormId: formId,
                },
            });
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [],
                applicationFormId: formId,
            },
        });
    };

    return {
        columns,
        rows,
        setRows,
        openPopup,
        setOpenPopup,
        defaultFormList,
        selectedRow,
        handleRowCreate,
        onRecordUpdate,
        handleEditClick,
        handleDelete,
        onEditSubmit,
        removeAllTempData,
        onDataEmpty,
        recoverDataCreation,
    };
};

export default useStudySiteOutOfWithHAData;
